import React, { useState, useReducer, useEffect, useCallback } from "react";
import $ from "./App.module.css";
import {
  Switch,
  Route,
  Link,
  useParams,
  useHistory,
  useLocation,
} from "react-router-dom";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlusHexagon,
  faTimesHexagon,
  faUndo,
  faSpinner,
  /*
  faShareSquare,
  faCommentPlus,
  faCloudDownload,
  faColumns,
  faChartLine,
  */
} from "@fortawesome/pro-regular-svg-icons";
import logo from "./logo.svg";
import { allEquipment, allParts } from "./Equipment.js";
import {
  FullAnalyses,
  Analyses,
  calculateExpenses,
  calculateHours,
  calculateParts,
} from "./Analyses.js";
import { Map, Set } from "immutable";
import { debounce, getState, reducer } from "./Reducer.js";
import { v4 as uuidv4 } from "uuid";
import Creatable from "react-select/creatable";

const BladeRow = ({ text, link, active, onClear }) => {
  return (
    <Link to={link} className={$.link}>
      <div
        className={classNames($.bladeRow, "animated", "fadeIn", {
          [`${$.bladeRowActive}`]: active,
        })}
      >
        <div className={$.bladeRowText}>{text}</div>
        <FontAwesomeIcon
          onClick={e => {
            onClear(e);
            e.stopPropagation();
            e.preventDefault();
          }}
          icon={faTimesHexagon}
        />
      </div>
    </Link>
  );
};

const BladeSelect = ({
  title,
  placeholder,
  onSelect,
  defaultResults,
  disabled,
}) => {
  const [value] = useState("");

  const options = defaultResults
    .sort()
    .map(x => {
      return { value: x, label: x };
    })
    .toJS();

  return (
    <div className={$.bladeInputContainer}>
      <div className={$.input}>
        <div className={$.inputTitle}>{title}</div>
        <Creatable
          placeholder={placeholder}
          isDisabled={disabled}
          value={value}
          onChange={({ value }) => onSelect(value)}
          options={options}
        />
      </div>
    </div>
  );
};

const PartsBlade = ({ dispatch, state }) => {
  const params = useParams();
  const history = useHistory();
  const { uuid, equipment } = params;
  const rows = state
    .getIn(["expenses", equipment], Map())
    .keySeq()
    .sortBy(x => x.toLowerCase())
    .map(part => (
      <BladeRow
        key={part}
        link={`/data/${uuid}/${equipment}/${part}`}
        text={part}
        active={params.part === part}
        onClear={e => {
          dispatch({ type: "DEL_PART", equipment, part });
          history.replace(`/data/${uuid}/${equipment}`);
        }}
      />
    ));

  return (
    <div>
      <div className={`${$.blade} animated fadeIn`}>
        <BladeSelect
          title={"Add Failing Part"}
          placeholder="(e.g. Gearbox, Bearing, Seal)"
          disabled={!equipment}
          defaultResults={allParts}
          onSelect={part => {
            history.push(`/data/${uuid}/${equipment}/${part}`);
            dispatch({ type: "ADD_PART", equipment, part });
          }}
        />
        {rows}
      </div>
    </div>
  );
};

const CostBlade = ({ state, dispatch }) => {
  const params = useParams();
  const { equipment, part } = params;
  const [isFlashing, setIsFlashing] = useState(false);

  const unflash = useCallback(
    debounce(() => setIsFlashing(false), 800),
    [],
  );

  const flash = useCallback(() => {
    setIsFlashing(true);
    unflash();
  }, [unflash]);

  const updateValue = k => e => {
    flash();
    dispatch({
      type: "ADD_PART_ATTR",
      equipment,
      part,
      attribute: k,
      value: e.target.value,
    });
  };

  const getValue = k => state.getIn(["expenses", equipment, part, k], "");

  return !params.part ? (
    <div />
  ) : (
    <div>
      <div className={`${$.blade} animated fadeIn`}>
        <div className={$.bladeInputContainer}>
          <div className={$.input}>
            <div className={$.inputTitle}>Hours to Recovery</div>
            <input
              placeholder="Hours"
              className={$.bladeInput}
              value={getValue("hours")}
              onChange={updateValue("hours")}
            />
          </div>
          <div className={$.input}>
            <div className={$.inputTitle}>Loss per Hour</div>
            <input
              placeholder="Dollars"
              className={$.bladeInput}
              value={getValue("lossPerHour")}
              onChange={updateValue("lossPerHour")}
            />
          </div>
          <div className={$.input}>
            <div className={$.inputTitle}>Occurrences per Year</div>
            <input
              placeholder="Occurrences"
              className={$.bladeInput}
              value={getValue("occurrences")}
              onChange={updateValue("occurrences")}
            />
          </div>
          <div
            className={
              isFlashing ? classNames($.spinner, "animated fadeIn") : "hidden"
            }
          >
            <FontAwesomeIcon
              icon={faSpinner}
              size="lg"
              spin
              className={$.icon}
            />
            Syncing your data
          </div>
        </div>
      </div>
    </div>
  );
};

const EquipmentBlade = ({ state, dispatch }) => {
  const params = useParams();
  const history = useHistory();
  const equipment = state.get("equipment");

  const equipmentRows = equipment
    .sortBy(x => x.toLowerCase())
    .map(eq => (
      <BladeRow
        key={eq}
        link={`/data/${params.uuid}/${eq}`}
        text={eq}
        active={params.equipment === eq}
        onClear={ev => {
          dispatch({ type: "DEL_EQUIPMENT", equipment: eq });
          history.replace(`/data/${params.uuid}`);
        }}
      />
    ));

  return (
    <div>
      <div className={`${$.blade} animated fadeIn`}>
        <BladeSelect
          title={"Equipment Type"}
          placeholder="(e.g. Pump, Agitator, Heater)"
          defaultResults={allEquipment}
          onSelect={v => {
            history.push(`/data/${params.uuid}/${v}`);
            dispatch({ type: "ADD_EQUIPMENT", equipment: v });
          }}
        />
        {equipmentRows}
      </div>
    </div>
  );
};

const Nav = ({ state }) => {
  const expenses = state.get("expenses");

  return (
    <div className={$.nav}>
      <div className={$.navLeft}>
        <div>
          <img className={$.logo} alt="reliability logo" src={logo} />
        </div>
      </div>
      <div className={$.navRight}>
        <div className={$.navBubble}>
          {calculateHours(expenses)}
          <span className={$.navBubbleUnit}>hours</span>
        </div>
        <div className={$.navBubble}>
          {state.get("expenses").keySeq().size}
          <span className={$.navBubbleUnit}>equipments</span>
        </div>
        <div className={$.navBubble}>
          {calculateParts(expenses)}
          <span className={$.navBubbleUnit}>failures</span>
        </div>
        <div className={$.navEquals}>=</div>
        <div className={classNames($.navBubble, $.navBubbleUrgent)}>
          <span className={$.navBubbleUnit}>$</span>
          {calculateExpenses(expenses)}
        </div>
      </div>
    </div>
  );
};

const LetsTalk = () => {
  return (
    <div className={$.letsTalk}>
      <a
        rel="noopener noreferrer"
        href="https://www.reliability.com/lets-talk-cfc"
        target="_blank"
        className={$.welcomeControl}
      >
        Let's Talk
      </a>
      <a
        rel="noopener noreferrer"
        href="https://www.reliability.com/cfc_suggestions"
        target="_blank"
        className={$.welcomeControl}
      >
        Suggestions?
      </a>
    </div>
  );
};

const DataPage = () => {
  const params = useParams();
  const location = useLocation();

  const [state, dispatch] = useReducer(
    reducer,
    new Map({
      uuid: params.uuid,
      expenses: Map(),
      equipment: Set(),
    }),
  );

  useEffect(() => {
    const updater = () => {
      getState(params.uuid, (err, newState) => {
        if (!err) {
          dispatch({ type: "UPDATE_STATE", state: newState });
        }
      });
    };

    updater();
    const id = setInterval(updater, 2000);
    return () => {
      clearInterval(id);
    };
  }, [params.uuid, dispatch]);

  return (
    <div className={$.dataPage}>
      <Nav state={state} />
      {location.pathname !== `/data/${state.get("uuid")}/analyses` ? (
        <div className={$.bladeContainer}>
          <EquipmentBlade state={state} dispatch={dispatch} />
          <PartsBlade state={state} dispatch={dispatch} />
          <CostBlade state={state} dispatch={dispatch} />
          <Analyses state={state} />
        </div>
      ) : (
        <FullAnalyses state={state} />
      )}
      <LetsTalk />
    </div>
  );
};

const WelcomePage = () => {
  const history = useHistory();

  const onNewClick = () => {
    const uuid = uuidv4();

    localStorage.setItem("uuid", uuid);
    history.push(`/data/${uuid}`);
  };

  const onResumeClick = e => {
    const uuid = localStorage.getItem("uuid");
    if (uuid) {
      history.push(`/data/${uuid}`);
    } else {
      e.stopPropagation();
    }
  };

  return (
    <div className={$.welcome}>
      <div className={classNames($.welcomeBody, "animated fadeIn")}>
        <div>
          <img className={$.welcomeLogo} alt="reliability logo" src={logo} />
        </div>
        <h4 className={$.welcomeTitle}>
          <span className={$.welcomeAccent}>Chronic Failure Calculator</span>
        </h4>
        <p className={$.welcomeCopy}>
          Reliability Engineers understand the incredible ROI that comes from
          being proactive versus reactive. This tool helps demonstrate the value
          of controlling the fix, instead of letting the fix control you.
          <br />
          <br />
          We hope this starts conversations that develop into cultures of
          Reliability at your plant.&nbsp;
          <b>
            To collaborate with others on your analysis, share your secure,
            generated link.
          </b>
        </p>
        <div className={$.welcomeControls}>
          <div className={$.welcomeControl} onClick={onNewClick}>
            <FontAwesomeIcon className={$.icon} icon={faPlusHexagon} />
            Start New Analysis
          </div>
          <div
            className={classNames($.welcomeControl, {
              [`${$.welcomeControlDisabled}`]: !localStorage.getItem("uuid"),
            })}
            onClick={onResumeClick}
          >
            <FontAwesomeIcon className={$.icon} icon={faUndo} />
            Resume Analysis
          </div>
        </div>
      </div>
    </div>
  );
};

export const MobileWarning = () => {
  return (
    <div className={$.mobileWarning}>
      <img alt="logo" src={logo} height={60} />
      <div className={$.mobileCopy}>
        <h3>Visit this on a Desktop</h3>
        <p>
          The <b>Chronic Failure Calculator</b> only works on desktop right now.
          Want a native mobile application to calculate ROI, build logic trees,
          track workplans, and analyze everything you need to build a culture of
          Reliability at your plant? Let us know below and we will give you
          access to our beta.
        </p>
        <a
          target="blank"
          style={{ display: "inline-block", marginTop: "30px" }}
          href="https://www.reliability.com/cfc_suggestions"
          className={$.welcomeControl}
        >
          Be a Beta Tester
        </a>
      </div>
    </div>
  );
};

export const App = () => {
  return (
    <div className={$.app}>
      <Switch>
        <Route path="/data/:uuid/:equipment/:part">
          <DataPage />
        </Route>
        <Route path="/data/:uuid/:equipment">
          <DataPage />
        </Route>
        <Route path="/data/:uuid">
          <DataPage />
        </Route>
        <Route path="/" exact>
          <WelcomePage />
        </Route>
      </Switch>
    </div>
  );
};
